<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <b-overlay :show="loading">
      <!-- Header -->
      <div class="min-h-screen bg-gray-50 p-8" id="rules-management">
        <!-- Header Section -->
        <div class="flex justify-between items-center mb-8" id="header">
          <h1 class="text-2xl font-semibold text-gray-800">
            Gestión de Reglas de Eventos en: {{ activeFolder.name }}
          </h1>
          <b-button
            title="Agregar nueva acción"
            v-b-tooltip.hover="'Agregar nueva regla'"
            variant="primary"
            class="btn-icon mt-2 text-center"
            @click="addRule()"
            icon="PlusIcon"
          >
            Nueva Regla
          </b-button>
        </div>

        <!-- Rules List -->
        <div class="bg-white rounded-xl shadow-sm" id="rules-list">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b">
              <tr>
                <th class="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Prioridad
                </th>
                <th class="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Nombre de la Regla
                </th>
                <th class="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Última Actualización
                </th>
                <th class="px-6 py-4 text-left text-sm font-medium text-gray-600">
                  Estado
                </th>
                <th class="px-6 py-4 text-right text-sm font-medium text-gray-600">
                  Acciones
                </th>
              </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
              <tr
                v-if="!loading && (!rules || rules.length == 0)"
                class="hover:bg-gray-50 text-center"
              >
                <td class="px-6 py-4 text-sm text-gray-800" colspan="5">
                  <strong>No se encontraron reglas</strong>
                </td>
              </tr>
              <tr
                class="hover:bg-gray-50"
                v-for="rule in rules"
                :key="rule.ruleId"
              >
                <td class="px-6 py-4 text-sm text-gray-800">{{ rule.priority || 0 }}</td>
                <td class="px-6 py-4 text-sm text-gray-800">{{ rule.name }}</td>
                <td class="px-6 py-4 text-sm text-gray-600">
                  {{ rule.updatedAt | dateTextFormat }}
                </td>
                <td class="px-6 py-4">
                  <span class="badge badge-success" v-if="rule.status === 'active'">
                      Activa
                  </span>
                  <span class="badge badge-danger" v-if="rule.status === 'deleted'">
                      Eliminada
                  </span>
                  <span class="badge badge-warning" v-if="rule.status === 'inactive'">
                      Inactiva
                  </span>
                </td>
                <td class="px-6 py-4 text-right">
                  <b-button
                    size="sm"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="success"
                    class="btn-icon"
                    title="Editar regla"
                    v-b-tooltip.hover="'Editar regla'"
                    v-on:click="editRule(rule.ruleId)"
                    :data-id="rule.ruleId"
                  >
                    <feather-icon
                      icon="EditIcon"
                      size="16"
                    />
                  </b-button>
                  <b-button
                    v-if="rule.status === 'inactive'"
                    size="sm"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="danger"
                    class="btn-icon ml-2"
                    title="Desactivar regla"
                    v-b-tooltip.hover="'Eliminar regla'"
                    v-on:click="deleteRule(rule.ruleId)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="16"
                    />
                  </b-button>
                  <b-button
                    v-if="rule.status === 'active'"
                    size="sm"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="warning"
                    class="btn-icon ml-2"
                    title="Desactivar regla"
                    v-b-tooltip.hover="'Desactivar regla'"
                    v-on:click="inactiveRule(rule.ruleId)"
                  >
                    <feather-icon
                      icon="LockIcon"
                      size="16"
                    />
                  </b-button>
                  <b-button
                    v-if="rule.status !== 'active'"
                    size="sm"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="info"
                    class="btn-icon ml-2"
                    title="Desactivar regla"
                    v-b-tooltip.hover="'Activar regla'"
                    v-on:click="activeRule(rule.ruleId)"
                  >
                    <feather-icon
                      icon="UnlockIcon"
                      size="16"
                    />
                  </b-button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { BButton } from 'bootstrap-vue';

export default {
  name: 'RulesView',
  data() {
    return {
      loading: true,
      ruleState: {}
    };
  },
  components: {
    BButton,
  },
  mounted() {
    this.loadRules();
  },
  computed: {
    ...mapGetters('Usuario', [
      'currentUser',
      'hasPermission'
    ]),
    ...mapGetters('RepositoryRules', [
      'getActiveFolder',
      'getRules'
    ]),
    activeFolder() {
      return this.getActiveFolder(this.$route.params.folderId);
    },
    rules() {
      return this.getRules(this.$route.params.folderId) || [];
    }
  },
  methods: {
    ...mapActions('RepositoryRules', [
      'getRulesFolder',
      'deleteRuleFolder',
      'updateFieldRuleFolder',
      'viewRule'
    ]),
    async loadRules() {
      this.loading = true;
      await this.getRulesFolder(
        {
          repositoryId: this.$route.params.repositoryId,
          folderId: this.$route.params.folderId,
          filters: 'status:!!!deleted'
        }
      );
      this.loading = false;
    },
    async deleteRule(ruleId) {
      this.loading = true;
      try {
        await this.deleteRuleFolder(
          {
            repositoryId: this.$route.params.repositoryId,
            folderId: this.$route.params.folderId,
            ruleId
          }
        );
        setTimeout(() => {
          this.loadRules();
        }, 1000);
      } catch (e) {
        this.loading = false;
      }
    },
    async inactiveRule(ruleId) {
      this.loading = true;
      try {
        await this.updateFieldRuleFolder(
          {
            repositoryId: this.$route.params.repositoryId,
            folderId: this.$route.params.folderId,
            ruleId,
            fields: {
              status: 'inactive'
            }
          }
        );
        setTimeout(() => {
          this.loadRules();
        }, 1000);
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    async activeRule(ruleId) {
      this.loading = true;
      try {
        await this.updateFieldRuleFolder(
          {
            repositoryId: this.$route.params.repositoryId,
            folderId: this.$route.params.folderId,
            ruleId,
            fields: {
              status: 'active'
            }
          }
        );
        setTimeout(() => {
          this.loadRules();
        }, 1000);
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    editRule(id) {
      this.viewRule(
        {
          router: this.$router,
          repositoryId: this.$route.params.repositoryId,
          folderId: this.$route.params.folderId,
          id
        }
      );
    },
    addRule() {
      this.viewRule(
        {
          router: this.$router,
          repositoryId: this.$route.params.repositoryId,
          folderId: this.$route.params.folderId,
          id: 'new'
        }
      );
    }
  },
  watch: {}
};
</script>

<style lang="scss" scoped>
.fb-bg-primary {
  background-color: rgb(var(--vs-primary)) !important;
}
</style>
